// STEP 5 · Product Detail Editor
// 판매자가 상품 상세페이지를 텍스트/상세이미지/HTML 블록 조합으로 편집.
// 기존 ImageUpload · SafeImg · lucide 아이콘 재사용.
// 저장 값: form.detail_blocks = [{ id, type: 'text'|'image'|'html', ...}]
// - text  : { text }
// - image : { url }
// - html  : { html }   ← 렌더 시 반드시 sanitize (ProductDetailRenderer 참조)
import React, { useCallback } from 'react';
import { Plus, Type, Image as ImgIcon, Code2, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';
import SafeImg from '@/components/SafeImg';

const uid = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

export default function ProductDetailEditor({ value = [], onChange }) {
  const blocks = Array.isArray(value) ? value : [];
  const emit = useCallback((next) => onChange?.(next), [onChange]);

  const addBlock = (type) => {
    const base = { id: uid(), type };
    const initial =
      type === 'text' ? { ...base, text: '' } :
      type === 'image' ? { ...base, url: '' } :
      { ...base, html: '' };
    emit([...blocks, initial]);
  };
  const patch = (idx, p) => emit(blocks.map((b, i) => (i === idx ? { ...b, ...p } : b)));
  const remove = (idx) => emit(blocks.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[j]] = [next[j], next[idx]];
    emit(next);
  };

  return (
    <div className="space-y-2" data-testid="product-detail-editor">
      <div className="flex items-center justify-between">
        <label className="text-xs font-black text-gray-600 dark:text-gray-300">
          상품 상세 설명 <span className="text-gray-400 font-normal">(선택 · 블록 조합)</span>
        </label>
        <div className="flex gap-1">
          <button type="button" onClick={() => addBlock('text')}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold hover:border-[#FF3B30]"
            data-testid="pde-add-text"><Type size={11}/> 텍스트</button>
          <button type="button" onClick={() => addBlock('image')}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold hover:border-[#FF3B30]"
            data-testid="pde-add-image"><ImgIcon size={11}/> 이미지</button>
          <button type="button" onClick={() => addBlock('html')}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold hover:border-[#FF3B30]"
            data-testid="pde-add-html"><Code2 size={11}/> HTML</button>
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="p-6 rounded-lg border border-dashed border-black/10 dark:border-white/10 text-center text-xs text-gray-500">
          <p className="mb-1">텍스트 · 상세 이미지 · HTML 을 원하는 순서로 조합해 상세페이지를 만드세요.</p>
          <p className="text-[11px] text-gray-400">상단 [텍스트/이미지/HTML] 버튼으로 블록을 추가할 수 있습니다.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <div key={b.id || i}
              className="relative rounded-lg border border-black/10 dark:border-white/10 p-3 bg-white/50 dark:bg-white/5"
              data-testid={`pde-block-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10">
                  {b.type === 'text' ? '텍스트' : b.type === 'image' ? '상세 이미지' : 'HTML'}
                  <span className="ml-1 text-gray-400 font-normal">#{i + 1}</span>
                </span>
                <div className="flex gap-0.5">
                  <button type="button" disabled={i === 0} onClick={() => move(i, -1)}
                    className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                    data-testid={`pde-up-${i}`} aria-label="위로"><ArrowUp size={11}/></button>
                  <button type="button" disabled={i === blocks.length - 1} onClick={() => move(i, 1)}
                    className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                    data-testid={`pde-down-${i}`} aria-label="아래로"><ArrowDown size={11}/></button>
                  <button type="button" onClick={() => remove(i)}
                    className="p-1 rounded text-red-500 hover:bg-red-50"
                    data-testid={`pde-del-${i}`} aria-label="삭제"><Trash2 size={11}/></button>
                </div>
              </div>

              {b.type === 'text' && (
                <textarea rows={4} value={b.text || ''}
                  onChange={(e) => patch(i, { text: e.target.value })}
                  placeholder="상품에 대한 설명 · 사이즈 · 소재 · 사용법 등"
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                  data-testid={`pde-text-${i}`}/>
              )}

              {b.type === 'image' && (
                <div className="flex items-start gap-3">
                  <div className="w-32 flex-shrink-0">
                    <ImageUpload kind="broadcast" value={b.url || ''}
                      onChange={(url) => patch(i, { url })}
                      data-testid={`pde-image-upload-${i}`}/>
                  </div>
                  {b.url && (
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-gray-500 mb-1">미리보기</p>
                      <SafeImg src={b.url} kind="broadcast" alt="detail"
                        className="max-w-full max-h-48 rounded object-contain bg-black/5"
                        data-testid={`pde-image-preview-${i}`}/>
                    </div>
                  )}
                </div>
              )}

              {b.type === 'html' && (
                <div>
                  <textarea rows={6} value={b.html || ''}
                    onChange={(e) => patch(i, { html: e.target.value })}
                    placeholder="기존 쇼핑몰 상세페이지 HTML 을 붙여넣으세요. <script>·<iframe>·onclick·javascript: 등 위험 요소는 저장·표시 시 자동 차단됩니다."
                    className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs font-mono"
                    data-testid={`pde-html-${i}`}/>
                  <p className="text-[10px] text-amber-600 mt-1">⚠ 스크립트·iframe·이벤트 핸들러는 소비자 화면에서 자동 제거됩니다.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
